import { useState, useCallback } from 'react';

/**
 * A hook that persists state to sessionStorage.
 *
 * State survives page navigation within the same tab/session,
 * but clears when the tab is closed.
 *
 * @param key - Storage key (should be unique per use case)
 * @param defaultValue - Default value when no stored value exists
 * @returns [value, setValue] tuple like useState
 *
 * @example
 * ```tsx
 * const [isExpanded, setIsExpanded] = useSessionState(
 *   `settings-expanded-${simulationId}`,
 *   false
 * );
 * ```
 */
export function useSessionState<T>(key: string, defaultValue: T): [T, (value: T) => void] {
  // Initialize from sessionStorage or use default
  const [state, setState] = useState<T>(() => {
    try {
      const stored = sessionStorage.getItem(key);
      if (stored !== null) {
        return JSON.parse(stored) as T;
      }
    } catch {
      // Ignore parse errors, use default
    }
    return defaultValue;
  });

  // Update both state and sessionStorage
  const setValue = useCallback(
    (value: T) => {
      setState(value);
      try {
        sessionStorage.setItem(key, JSON.stringify(value));
      } catch {
        // Ignore storage errors (quota exceeded, etc.)
      }
    },
    [key],
  );

  return [state, setValue];
}
